import React from 'react';
import { getWindDirectionLabel } from 'screens/Applications&WorkOrders/StepsLayout/Components/WeatherStep/helpers/windDirectionMapping';
import {
  formatTemperatureDisplay,
  formatHumidityDisplay,
  roundWeatherValue
} from 'helpers/weatherDisplayHelpers';

interface WeatherData {
  windSpeed?: number;
  windSpeedUnit?: string;
  windDirection?: string;
  skyCondition?: string;
  temperature?: number;
  temperatureUnit?: string;
  soilMoisture?: string;
  humidity?: number;
}

interface WeatherSectionProps {
  title: string;
  dateTime?: string;
  weatherData?: WeatherData;
}

const WeatherSection = ({
  title,
  dateTime,
  weatherData
}: WeatherSectionProps) => {
  const formatValue = (value: any): string => {
    if (value === undefined || value === null || value === '') {
      return '---';
    }
    return String(value);
  };

  const formatWind = (): string => {
    const speed = weatherData?.windSpeed;
    const unit = weatherData?.windSpeedUnit;
    const direction = weatherData?.windDirection;

    // If no speed and no direction, return empty state
    if ((speed === undefined || speed === null) && !direction) {
      return '---';
    }

    const parts: string[] = [];

    // Add direction with full label if available
    if (direction) {
      const directionLabel = getWindDirectionLabel(direction);
      parts.push(directionLabel);
    }

    // Add speed with unit if available - rounded to whole number
    const roundedSpeed = roundWeatherValue(speed);
    if (roundedSpeed !== undefined) {
      const unitDisplay = unit?.toUpperCase() || 'MPH';
      // Only add "at" if there's a direction
      const speedText = direction
        ? `at ${roundedSpeed} ${unitDisplay}`
        : `${roundedSpeed} ${unitDisplay}`;
      parts.push(speedText);
    }

    return parts.length > 0 ? parts.join(' ') : '---';
  };

  const formatTemperature = (): string => {
    return formatTemperatureDisplay(
      weatherData?.temperature,
      weatherData?.temperatureUnit
    );
  };

  const weatherItems = [
    {
      key: 'sky-condition',
      label: 'Sky Condition',
      value: formatValue(weatherData?.skyCondition)
    },
    {
      key: 'temperature',
      label: 'Temperature',
      value: formatTemperature()
    },
    {
      key: 'humidity',
      label: 'Relative Humidity',
      value: formatHumidityDisplay(weatherData?.humidity)
    },
    {
      key: 'wind',
      label: 'Wind',
      value: formatWind()
    },
    {
      key: 'soil-moisture',
      label: 'Soil Moisture',
      value: formatValue(weatherData?.soilMoisture)
    }
  ];

  return (
    <div className="flex flex-col">
      <div className="mb-4">
        <div className="text-sm font-semibold text-neutral-60">
          {title}
          {dateTime && `: ${dateTime}`}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {weatherItems.map(({ key, label, value }) => (
          <div
            key={key}
            className="flex items-start gap-5 py-2 border-b border-neutral-20 weather-item"
          >
            <div
              className="text-sm text-neutral-60 weather-label"
              data-testid={`${key}-label`}
            >
              {label}
            </div>
            <div
              className="text-sm text-neutral-1000"
              data-testid={`${key}-value`}
            >
              {value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WeatherSection;
